<script setup lang="ts">
import { toHead } from 'vue-datocms'
import { queryLocation } from '~/graphql/queries'
import type { StringMultiLocaleField, BooleanTypeNonNullMultiLocaleField } from '~/types/datocms'
import { ApiErrorType } from '~/types'
import type { SectionComponents } from '~/types/components'

const { throwError, handleApiError } = useApiError()
const { resolveSectionComponent } = useResolveComponent()

const appStore = useAppStore()
const { locale } = useI18n()
const route = useRoute()

const { data, error } = await useDatoQuery(queryLocation, {
  variables: {
    locale: locale.value,
    slug: route.params.slug as string
  }
})

const currentTime = ref('')
const timeZone = ref('')
const amPm = ref('')

const updateTime = () => {
  const date = new Date()

  const options = {
    timeZone: 'Europe/Ljubljana',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }

  const formattedTime = new Intl.DateTimeFormat('en-US', options).format(date)

  const [time, period] = formattedTime.split(' ')

  const isDST = date.getMonth() > 2 && date.getMonth() < 10
  const zone = isDST ? 'CEST' : 'CET' // Manually handle CET or CEST based on month

  currentTime.value = time
  amPm.value = period
  timeZone.value = zone
}

updateTime()

useIntervalFn(updateTime, 10000, { immediate: true })

const currentMonth = new Date().toLocaleString('en-US', { month: 'long' })
const currentMonthData = data.value?.location?.weather?.find((data) => data.month === currentMonth)
const averageTemperature = currentMonthData
  ? (currentMonthData.highTemperature + currentMonthData.lowTemperature) / 2
  : undefined

function celsiusToFahrenheit(celsius: number) {
  return (celsius * 9) / 5 + 32
}

if (error.value) {
  handleApiError(error.value)
}

if (!data.value?.location) {
  throwError(ApiErrorType.NOT_FOUND)
}

// Dynamic route parameters
const setI18nParams = useSetI18nParams()
const publishedLocales = useLocaleSlugs(
  data.value?.location?._allSlugLocales as StringMultiLocaleField[],
  data.value?.location?._allPublishTranslationLocales as BooleanTypeNonNullMultiLocaleField[]
)
setI18nParams(publishedLocales)

if (data.value?.location?.seoMetadata?.noIndex || !data.value?.location?.publishTranslation) {
  useRobotsRule('noindex, nofollow')
}

useHead(() => {
  return toHead(data.value?.location?._seoMetaTags || [])
})

onMounted(() => {
  appStore.setTheme('dark')
  appStore.setGradientHeader('cream')
  appStore.setShowFooter(false)
})

onBeforeUnmount(() => {
  appStore.setShowFooter(true)
})
</script>

<template>
  <article v-if="data">
    <div class="bg-gradient-light bg-cream pb-10 pt-20 lg:pt-30">
      <div class="mb-6 lg:container lg:mb-4">
        <Heading
          v-if="data?.location?.title"
          :level="1"
          :size="2"
          class="px-4 font-light text-yellow lg:px-0"
          :is-semantic="true"
          >{{ data?.location?.title }}</Heading
        >
      </div>
      <LazySwiperLocationGallery
        v-if="data.location?.imageGallery?.length"
        :data="data.location?.imageGallery"
      />
    </div>
    <div class="bg-cream pb-8 pt-4 font-mono text-yellow lg:pb-30 lg:pt-8">
      <div class="container grid-default justify-between">
        <div
          v-if="data.location?.elevation"
          class="col-span-full flex flex-row gap-4 lg:col-span-4 lg:flex-col lg:gap-0"
        >
          <span class="flex-1 text-3.5xl lg:order-2 lg:flex-auto lg:text-6xl">{{
            data.location?.elevation
          }}</span>
          <span class="flex-1 uppercase lg:order-1 lg:flex-auto">APPROX. ELEVATION</span>
        </div>
        <div
          class="col-span-full flex flex-col justify-between gap-4 lg:col-span-7 lg:col-start-6 lg:flex-row 2xl:col-span-5 2xl:col-start-8"
        >
          <div v-if="currentTime" class="flex flex-row gap-4 lg:flex-col lg:gap-2">
            <div class="flex flex-1 flex-col lg:order-2 lg:flex-auto lg:gap-2">
              <span class="text-3.5xl lg:text-6xl"> {{ currentTime }}</span>
              <span class="hidden items-center justify-between uppercase lg:inline-flex lg:text-xl">
                <span>{{ timeZone }}</span>
                <span>{{ amPm }}</span>
              </span>
            </div>
            <span class="inline-flex flex-1 flex-col uppercase lg:order-1 lg:flex-auto">
              Local time
              <span class="flex gap-2 lg:hidden">
                <span>{{ timeZone }}</span>
                <span>{{ amPm }}</span>
              </span>
            </span>
          </div>
          <div v-if="averageTemperature" class="flex flex-row gap-4 lg:flex-col lg:gap-2">
            <div class="flex flex-1 flex-col lg:order-2 lg:flex-auto lg:gap-2">
              <span class="text-3.5xl lg:text-6xl"
                >{{ averageTemperature }}<span class="font-sans">&#8451;</span></span
              >
              <span class="hidden lg:flex lg:text-xl"
                ><span class="pr-1">{{ celsiusToFahrenheit(averageTemperature) }}</span>
                <span class="font-sans">&#8457;</span></span
              >
            </div>
            <span class="flex flex-1 flex-col uppercase lg:order-1 lg:flex-auto">
              <span>AVERAGE TEMP.</span>
              <span class="lg:hidden"
                >({{ celsiusToFahrenheit(averageTemperature) }}
                <span class="font-sans">&#8457;</span>)</span
              >
            </span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="data.location.html" class="bg-cream py-4 text-black lg:py-10">
      <div class="container grid-default">
        <div
          class="col-span-full text-lg uppercase lg:col-span-6"
          v-html="data.location.html"
        ></div>
      </div>
    </div>
    <div v-if="Array.isArray(data?.location?.blocks) && data?.location?.blocks.length">
      <component
        :is="resolveSectionComponent(section.__typename as SectionComponents)"
        v-for="(section, idx) in data.location.blocks"
        :key="section.__typename"
        :module-index="idx + 1"
        :data="section"
      />
    </div>
    <div class="bg-cream text-yellow">
      <LocationNext
        class="mx-auto max-w-[912px] px-4 2xl:max-w-[1556px]"
        :current-record-slug="data.location?.slug"
        :all-records="data.allLocations"
        :all-records-count="data._allLocationsMeta.count"
      />
    </div>
  </article>
</template>
